import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { Card, CardHeader, Table, Th, Tr, Td, Select, Spinner, EmptyState, Badge } from '../components/ui';
import { fmtCurrencyWhole, fmtDateTime } from '../lib/fmt';
import { StatementView } from '../components/FpaStatement';
import { LineCodeMapSelect } from '../components/LineCodeMapSelect';

function defaultMonths(): { from: string; to: string } {
  const d = new Date();
  const to = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const s = new Date(d.getFullYear(), d.getMonth() - 11, 1);
  const from = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}`;
  return { from, to };
}

const monthInputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};

export default function AdminFpaActualsPage() {
  const init = defaultMonths();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  // Actuals endpoint drives the mapping-health panel below; the P&L itself reads
  // the unified statement (actuals-only column) so it matches the Plan & Variance.
  const { data, refetch } = useQuery(() => api.fpa.actuals(from, to), [from, to]);
  const stmt = useQuery(() => api.fpa.statement({ from, to, plan: [], includeActual: true }), [from, to]);
  const { user } = useAuth();
  const canMap = user?.fpa_role === 'analyst' || user?.fpa_role === 'approver';
  const depts = useQuery(() => api.fpa.departments());
  const deptNames = Object.fromEntries((depts.data?.departments ?? []).map((d) => [d.id, d.name]));

  return (
    <>
      <Card>
        <CardHeader>
          <div style={{ fontSize: 13, fontWeight: 500 }}>FP&amp;A actuals</div>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>From</span>
            <input type="month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={monthInputStyle} />
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>to</span>
            <input type="month" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={monthInputStyle} />
          </span>
        </CardHeader>
        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-text-muted)' }}>
          Recognized P&amp;L from QuickBooks, mapped to FP&amp;A line codes. Each line expands (▸) to its
          departments; costs read as negatives so the column is an income statement.
          {stmt.data?.freshness.last_synced_at
            ? <> Last synced {fmtDateTime(stmt.data.freshness.last_synced_at)}.</>
            : <> No QBO sync yet.</>}
        </div>
        {stmt.isLoading ? <Spinner />
          : stmt.error ? <EmptyState message={stmt.error} />
          : !stmt.data || stmt.data.rows.length === 0 ? <EmptyState message="No actuals for this range. Connect QBO and map accounts on the QBO mapping page." />
          : <StatementView statement={stmt.data} deptNames={deptNames} />}
      </Card>

      {data && (data.unmapped.accounts.length > 0 || data.unmapped.classes.length > 0) && (
        <Card>
          <CardHeader>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Unmapped</div>
            <span style={{ fontSize: 11, color: 'var(--color-amber-text)' }}>
              excluded from / uncategorised in the totals above — {canMap ? 'map them inline below or on the QBO page' : 'map them on the QBO mapping page'}
            </span>
          </CardHeader>
          {data.unmapped.accounts.length > 0 && (
            <Table>
              <thead><tr><Th>Unmapped account</Th><Th>Classification</Th><Th right>Amount</Th>{canMap && <Th>Map to FP&A line</Th>}</tr></thead>
              <tbody>
                {data.unmapped.accounts.map((a) => (
                  <Tr key={a.qbo_account_id}>
                    <Td>{a.name ?? a.qbo_account_id}</Td>
                    <Td muted>{a.classification ?? '—'} <Badge variant="amber">no line</Badge></Td>
                    <Td right>{fmtCurrencyWhole(a.amount)}</Td>
                    {canMap && <Td><LineCodeMapSelect qboAccountId={a.qbo_account_id} lineCode={null} classification={a.classification} onSaved={refetch} /></Td>}
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
          {data.unmapped.classes.length > 0 && (
            <Table>
              <thead><tr><Th>Class with no department</Th><Th right>Amount</Th>{canMap && <Th>Map to department</Th>}</tr></thead>
              <tbody>
                {data.unmapped.classes.map((c) => (
                  <Tr key={c.qbo_class_id}>
                    <Td>{c.name ?? c.qbo_class_id} <Badge variant="amber">no dept</Badge></Td>
                    <Td right>{fmtCurrencyWhole(c.amount)}</Td>
                    {canMap && (
                      <Td>
                        <Select value="" onChange={async (v) => { if (!v) return; await api.fpa.setDepartmentMap(c.qbo_class_id, v); refetch(); }}>
                          <option value="">— assign department —</option>
                          {(depts.data?.departments ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </Select>
                      </Td>
                    )}
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}
    </>
  );
}
